import React, { useEffect, useState } from "react";
import { List, ListItem, ListItemText, Typography, Box } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Attendee,
  Schedule,
  AttendeeToSchedule,
} from "../../../redux/types/types";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import AttendeeSchedulesModal from "./AttendeeSchedulesModal/AttendeeSchedulesModal";
import {
  getAttendeeAccessDisplay,
  shouldShowCounterForClass,
} from "../../../utils/specialClassAccess";
import AttendeeImportantInfoButton from "../../Attendees/AttendeeImportantInfoButton";

interface AttendeesListProps {
  attendeesList: Attendee[];
  classId: number;
  schedulesList: Schedule[];
  onClose: () => void;
}

export default function AttendeesList({
  attendeesList,
  classId,
  schedulesList,
  onClose,
}: AttendeesListProps) {
  const dispatch = useDispatch<AppDispatch>();
  const intl = useIntl();
  const isInstructor = useSelector(
    (state: RootState) => state.auth.isInstructor,
  );
  const isAdmin = useSelector((state: RootState) => state.auth.isAdmin);
  const userClasses = useSelector(
    (state: RootState) => state.auth.user.classes || [],
  );
  const adminClasses = useSelector(
    (state: RootState) => state.admin.classes || [],
  );

  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [selectedAttendee, setSelectedAttendee] =
    useState<AttendeeToSchedule | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const handleAttendeeClick = (attendee: Attendee) => {
    if (!isInstructor) {
      setSelectedAttendee({
        attendee,
        createdAt: new Date().toISOString(),
        unenrollDate: null,
      });
      setModalOpen(true);
    }
  };

  useEffect(() => {
    setAttendees(attendeesList);
  }, [attendeesList, dispatch]);

  return (
    <>
      <List sx={{ minWidth: 150, maxWidth: 350, maxHeight: "50vh" }}>
        <ListItem>
          <ListItemText
            primary={intl.formatMessage({
              id: "myClasses.attendeesList",
              defaultMessage: "Attendees List",
            })}
            primaryTypographyProps={{
              variant: "subtitle1",
              fontWeight: "bold",
            }}
          />
        </ListItem>
        {attendees.map((attendee, index) => (
          <ListItem
            key={attendee.id}
            onClick={() => handleAttendeeClick(attendee)}
            sx={{
              cursor: isInstructor ? "default" : "pointer",
              flexDirection: "column",
              alignItems: "stretch",
            }}
          >
            <ListItemText
              primary={
                <Typography>
                  {`${index + 1}. ${attendee.firstName} ${attendee.lastName}`}
                </Typography>
              }
              secondary={
                <Box>
                  <Box
                    sx={{ border: "1px solid", borderColor: "divider" }}
                  ></Box>
                  <Typography variant="body2" component="span">
                    {`${calculateAge(attendee.birthdate)} ${intl.formatMessage({
                      id: "attendee.yearsOld",
                    })}, ${
                      attendee.sex === "female"
                        ? intl.formatMessage({
                            id: "privateEnroll.female",
                          })
                        : attendee.sex === "male"
                          ? intl.formatMessage({
                              id: "privateEnroll.male",
                            })
                          : attendee.sex === "other"
                            ? intl.formatMessage({
                                id: "privateEnroll.other",
                              })
                            : attendee.sex
                    }`}
                  </Typography>
                  <Box
                    sx={{ border: "1px solid", borderColor: "divider" }}
                  ></Box>
                  <Typography variant="body2" color="text.secondary">
                    <FormattedMessage id="attendee.permisStatus.title" />:{" "}
                    {(attendee.permisStatus === "PermisS" &&
                      intl.formatMessage({
                        id: "attendee.permisStatus.permisS",
                      })) ||
                      (attendee.permisStatus === "Other" &&
                        intl.formatMessage({
                          id: "attendee.permisStatus.other",
                        }))}
                  </Typography>
                  {(() => {
                    const classesData = isAdmin ? adminClasses : userClasses;
                    const currentClass = classesData.find(
                      (cls: any) => cls.id === classId,
                    );

                    // Check if attendee is actually enrolled (not unenrolled)
                    // If unenrollDate is set, they are unenrolled
                    const isEnrolled = !attendee.unenrollDate;

                    // Use shouldShowCounterForClass to determine if we should display
                    if (
                      shouldShowCounterForClass(
                        attendee,
                        currentClass,
                        isEnrolled,
                      )
                    ) {
                      return (
                        <>
                          <Box
                            sx={{ border: "1px solid", borderColor: "divider" }}
                          ></Box>
                          <Typography
                            variant="body2"
                            component="div"
                            color="text.secondary"
                          >
                            {getAttendeeAccessDisplay(
                              attendee,
                              currentClass,
                              intl.formatMessage,
                            )}
                          </Typography>
                        </>
                      );
                    }
                    return null;
                  })()}
                </Box>
              }
            />
            <AttendeeImportantInfoButton attendee={attendee} />
          </ListItem>
        ))}
        {attendeesList.length === 0 && (
          <ListItem>
            <ListItemText
              primary={
                <FormattedMessage
                  id="myClasses.noAttendees"
                  defaultMessage="No attendees"
                />
              }
            />
          </ListItem>
        )}
      </List>
      <AttendeeSchedulesModal
        listClose={onClose}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        attendee={selectedAttendee}
        schedulesList={schedulesList}
      />
    </>
  );
}

function calculateAge(birthdate: Date): number {
  const today = new Date();
  const birthDate = new Date(birthdate);
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}
